import React, { useEffect, useState } from "react";
import { http, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useAuthDialog } from "@/components/AuthDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import ShareButton from "@/components/ShareButton";
import { Calendar, MapPin, Users, Plus, X, Loader2, CheckCircle2, Waves, Sparkles, Music, Utensils, MoonStar, PartyPopper } from "lucide-react";

/** Category → icon + label + accent color. */
const CATS = {
  surf:  { icon: Waves,       label: "Surf",   color: "#2EC4B6" },
  yoga:  { icon: Sparkles,    label: "Yoga",   color: "#FFB84D" },
  music: { icon: Music,       label: "Music",  color: "#7C4DFF" },
  moon:  { icon: MoonStar,    label: "Moon",   color: "#1B3B6F" },
  food:  { icon: Utensils,    label: "Food",   color: "#E76F51" },
  party: { icon: PartyPopper, label: "Party",  color: "#FF6F61" },
  other: { icon: Calendar,    label: "Other",  color: "#5C757D" },
};

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}

export default function EventsSection() {
  const { user } = useAuth();
  const { open: openAuth } = useAuthDialog();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    try {
      const { data } = await http.get("/events");
      setEvents(data);
    } catch (_e) {
      // silent — section just shows empty state
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleRsvp = async (ev) => {
    if (!user?.id) {
      openAuth("login");
      return;
    }
    try {
      const method = ev.rsvped ? "delete" : "post";
      const { data } = await http[method](`/events/${ev.id}/rsvp`);
      setEvents((arr) => arr.map((e) => e.id === ev.id ? data : e));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "RSVP failed");
    }
  };

  return (
    <section
      id="events"
      data-testid="events-section"
      className="clay p-5 sm:p-6 scroll-mt-24"
    >
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-8 h-8 rounded-full bg-[color:var(--coral)]/10 text-[color:var(--coral)] grid place-items-center">
          <Calendar className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <p className="eyebrow text-[color:var(--coral)]">Community events</p>
          <h3 className="text-lg font-bold" style={{ fontFamily: "Outfit" }}>
            What&apos;s happening in Popoyo
          </h3>
        </div>
        <ShareButton
          path="/#events"
          variant="ghost"
          label="Share"
          title="Community events — Popoyo.app"
          text="What's happening in Popoyo this week."
          testId="share-events-btn"
        />
        <Button
          onClick={() => (user?.id ? setShowCreate(true) : openAuth("login"))}
          data-testid="event-create-open"
          className="pill bg-[color:var(--coral)] text-white hover:bg-[color:var(--coral)]/90"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Propose event
        </Button>
      </div>

      <div className="mt-4">
        {loading ? (
          <div className="flex items-center gap-2 text-[color:var(--ink-2)] text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading upcoming events…
          </div>
        ) : events.length === 0 ? (
          <p className="text-sm text-[color:var(--ink-2)] italic" data-testid="events-empty">
            No upcoming events yet. Propose one and it&apos;ll go live after admin review.
          </p>
        ) : (
          <ul className="space-y-3" data-testid="events-list">
            {events.map((ev) => {
              const cat = CATS[ev.category] || CATS.other;
              const Icon = cat.icon;
              return (
                <li
                  key={ev.id}
                  data-testid={`event-${ev.id}`}
                  className="rounded-2xl border border-[color:var(--ink-2)]/10 p-4 bg-white flex items-start gap-3 flex-wrap md:flex-nowrap"
                >
                  <div
                    className="w-10 h-10 rounded-full grid place-items-center shrink-0"
                    style={{ background: `${cat.color}22`, color: cat.color }}
                    aria-hidden
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-[220px]">
                    <p
                      className="font-semibold text-[color:var(--ink)] leading-snug"
                      style={{ fontFamily: "Outfit" }}
                    >
                      {ev.title}
                    </p>
                    <p className="text-xs text-[color:var(--ink-2)] mt-0.5 flex items-center gap-1.5 flex-wrap">
                      <span style={{ color: cat.color, fontWeight: 600 }}>{cat.label.toUpperCase()}</span>
                      <span>·</span>
                      <span>{fmtDate(ev.start_at)}</span>
                      {ev.location && (<><span>·</span><span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{ev.location}</span></>)}
                    </p>
                    {ev.description && (
                      <p className="text-sm text-[color:var(--ink)] mt-1.5">{ev.description}</p>
                    )}
                    <p className="text-[11px] text-[color:var(--ink-2)] mt-1">
                      Posted by <strong>{ev.created_by}</strong>
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <span className="text-[11px] text-[color:var(--ink-2)] flex items-center gap-1">
                      <Users className="w-3 h-3" /> {ev.rsvp_count} going
                    </span>
                    <Button
                      onClick={() => toggleRsvp(ev)}
                      data-testid={`event-rsvp-${ev.id}`}
                      variant={ev.rsvped ? "outline" : "default"}
                      className={ev.rsvped
                        ? "pill border-[color:var(--teal)] text-[color:var(--teal)]"
                        : "pill bg-[color:var(--coral)] text-white hover:bg-[color:var(--coral)]/90"}
                    >
                      {ev.rsvped ? (<><CheckCircle2 className="w-4 h-4 mr-1.5" /> Going</>) : "RSVP"}
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {showCreate && (
        <CreateEventDialog
          onClose={() => setShowCreate(false)}
          onCreated={(newDoc) => {
            if (newDoc.status === "approved") {
              setEvents((arr) => [newDoc, ...arr]);
              toast.success("Event live!");
            } else {
              toast.success("Event submitted — pending admin approval");
            }
          }}
        />
      )}
    </section>
  );
}

function CreateEventDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({
    title: "", description: "", category: "surf", start_at: "", location: "", beach_id: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.start_at) {
      toast.error("Title and date/time are required");
      return;
    }
    setBusy(true);
    try {
      const iso = new Date(form.start_at).toISOString();
      const { data } = await http.post("/events", {
        title: form.title,
        description: form.description,
        category: form.category,
        start_at: iso,
        location: form.location,
        beach_id: form.beach_id || null,
      });
      onCreated(data);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not create event");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 backdrop-blur-sm px-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        data-testid="event-create-dialog"
        className="clay bg-white w-full max-w-lg p-5 sm:p-6 space-y-3"
      >
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-[color:var(--coral)]" />
          <h3 className="text-lg font-bold" style={{ fontFamily: "Outfit" }}>Propose a community event</h3>
          <button type="button" onClick={onClose} className="ml-auto text-[color:var(--ink-2)] hover:text-[color:var(--coral)]" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <Input
          data-testid="event-title-input"
          placeholder="Event title (e.g. Full Moon Yoga @ Guasacate)"
          value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          className="rounded-2xl"
        />
        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs flex flex-col gap-1">
            <span className="text-[color:var(--ink-2)]">Category</span>
            <select
              data-testid="event-category-select"
              value={form.category}
              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              className="rounded-2xl border border-[color:var(--hairline)] px-3 py-2 bg-white"
            >
              {Object.entries(CATS).map(([k, v]) => (<option key={k} value={k}>{v.label}</option>))}
            </select>
          </label>
          <label className="text-xs flex flex-col gap-1">
            <span className="text-[color:var(--ink-2)]">Date &amp; time</span>
            <Input
              data-testid="event-start-input"
              type="datetime-local"
              value={form.start_at}
              onChange={(e) => setForm((f) => ({ ...f, start_at: e.target.value }))}
              className="rounded-2xl"
            />
          </label>
        </div>
        <Input
          data-testid="event-location-input"
          placeholder="Location (e.g. Playa Popoyo beachfront)"
          value={form.location}
          onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
          className="rounded-2xl"
        />
        <Textarea
          data-testid="event-description-input"
          placeholder="Describe the event, what to bring, cost, contact info…"
          value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          rows={4}
          className="rounded-2xl"
        />
        <p className="text-[11px] text-[color:var(--ink-2)]">
          Community events go live after a quick admin review (usually within a few hours).
        </p>
        <Button type="submit" disabled={busy} data-testid="event-submit-btn" className="pill w-full bg-[color:var(--coral)] text-white hover:bg-[color:var(--coral)]/90">
          {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
          Submit event
        </Button>
      </form>
    </div>
  );
}
